import { Plus, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { RegistrationForm, RegServiceLocation } from "./types";
import { REGIONS, SERVICE_CATEGORIES } from "./types";
import CityAutocomplete from "./CityAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const inputClass =
  "w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors";
const selectClass = inputClass;
const labelClass = "text-micro block mb-2";
const RADIUS_OPTIONS = [10, 25, 50, 75, 100, 150, 200];

interface Props {
  form: RegistrationForm;
  update: (field: string, value: any) => void;
  toggleArrayItem: (field: string, item: string) => void;
}

export default function RegService({ form, update, toggleArrayItem }: Props) {
  const { data: craftTrades = [], isLoading: tradesLoading } = useQuery({
    queryKey: ["craft-trades"],
    queryFn: async () => {
      const { data } = await supabase
        .from("craft_trades")
        .select("id, key, label")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const addLocation = () => {
    const loc: RegServiceLocation = { id: crypto.randomUUID(), city: "", postal_code: "", radius_km: 50 };
    update("service_locations", [...form.service_locations, loc]);
  };

  const removeLocation = (id: string) => {
    update("service_locations", form.service_locations.filter((l) => l.id !== id));
  };

  return (
    <div className="space-y-8">
      {/* Gewerke */}
      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">Gewerke</h3>
        {tradesLoading ? (
          <p className="text-caption">Gewerke werden geladen...</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {craftTrades.map((trade) => (
              <button
                key={trade.key}
                type="button"
                onClick={() => toggleArrayItem("categories", trade.key)}
                className={cn(
                  "px-4 py-2 text-sm border transition-colors",
                  form.categories.includes(trade.key)
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}
              >
                {trade.label}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Regionen */}
      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">Regionen & Einsatzgebiet</h3>
        <div>
          <label className={labelClass}>Hauptregion</label>
          <select value={form.region} onChange={(e) => update("region", e.target.value)} className={selectClass}>
            <option value="">Bitte wählen</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Service-Regionen</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {REGIONS.map((r) => (
              <button key={r} type="button" onClick={() => toggleArrayItem("service_regions", r)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  form.service_regions.includes(r) ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}>{r}</button>
            ))}
          </div>
        </div>

        {/* Einsatzorte */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className={labelClass + " !mb-0"}>Einsatzorte mit Umkreis</label>
              <p className="text-caption">Optional: Standorte mit Einsatzradius</p>
            </div>
            <Button type="button" variant="quiet" size="sm" onClick={addLocation}>
              <Plus className="w-4 h-4 mr-1" /> Standort
            </Button>
          </div>
          {form.service_locations.length === 0 && (
            <div className="p-6 border border-dashed border-border text-center">
              <MapPin className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
              <p className="text-caption">Optional: Einsatzorte für präziseres Matching</p>
            </div>
          )}
          {form.service_locations.map((loc, idx) => (
            <div key={loc.id} className="border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-micro">Standort {idx + 1}</span>
                <button type="button" onClick={() => removeLocation(loc.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Stadt *</label>
                  <CityAutocomplete
                    value={loc.city}
                    postalCode={loc.postal_code}
                    onSelect={(city, plz) => {
                      update("service_locations", form.service_locations.map((l) =>
                        l.id === loc.id ? { ...l, city, postal_code: plz } : l
                      ));
                    }}
                    placeholder="z.B. München"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>PLZ</label>
                  <input value={loc.postal_code} readOnly className={inputClass + " bg-muted/50 cursor-not-allowed"} tabIndex={-1} />
                </div>
                <div>
                  <label className={labelClass}>Umkreis</label>
                  <select value={loc.radius_km} onChange={(e) => {
                    update("service_locations", form.service_locations.map((l) =>
                      l.id === loc.id ? { ...l, radius_km: Number(e.target.value) } : l
                    ));
                  }} className={selectClass}>
                    {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.remote_supported} onChange={(e) => update("remote_supported", e.target.checked)} className="w-4 h-4 accent-accent" />
          <span className="text-sm">Remote-Support möglich</span>
        </label>
      </section>

      {/* SLA */}
      <section className="border border-border bg-card p-6 space-y-6">
        <h3 className="text-subheadline">Reaktionszeit & Kontingente</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Reaktionszeit</label>
            <select value={form.sla_tier} onChange={(e) => update("sla_tier", e.target.value)} className={selectClass}>
              <option value="2H">2 Stunden</option>
              <option value="4H">4 Stunden</option>
              <option value="NBD">Next Business Day</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Reaktionszeit (h)</label>
            <input type="number" value={form.sla_response_hours} onChange={(e) => update("sla_response_hours", Number(e.target.value))} min={1} max={72} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Wochenlimit Anfragen</label>
            <input type="number" value={form.weekly_lead_limit} onChange={(e) => update("weekly_lead_limit", Number(e.target.value))} min={1} max={50} className={inputClass} />
          </div>
        </div>
      </section>
    </div>
  );
}
